import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootDispatch, RootState } from "../../../../store";
import {
  acceptMaterialTransfers,
  cancelMaterialTransfers,
  clearMaterialTransferError,
  createMaterialTransfer,
  fetchPendingIncomingMaterialTransfers,
  fetchPendingOutgoingMaterialTransfers,
} from "../../../../store/slices/StockTransfer/materialStockTransferSlice";
import { CreateMaterialTransferPayload } from "../../../../models/materialStockTransfer.model";
import toast from "react-hot-toast";
import CreateMaterialTransferSection from "./CreateMaterialTransferSection";
import MaterialTransferList from "./MaterialTransferList";

interface WarehouseOption {
  value: string;
  label: string;
}

interface MaterialTransferTabProps {
  warehouseOptions: WarehouseOption[];
}

const MaterialTransferTab: React.FC<MaterialTransferTabProps> = ({
  warehouseOptions,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [materialsRefreshKey, setMaterialsRefreshKey] = useState(0);
  const activeDirection =
    searchParams.get("materialTransferTab") === "outgoing"
      ? "outgoing"
      : "incoming";
  const { incoming, outgoing, actionLoading, error } = useSelector(
    (state: RootState) => state.materialStockTransfer,
  );
  const role = useSelector((state: RootState) => state.auth.role);
  const userId = useSelector((state: RootState) => state.auth.userId);
  const warehouseAssigned = useSelector(
    (state: RootState) => state.auth.warehouseAssigned,
  );
  const isAdmin = role === "admin";

  useEffect(() => {
    dispatch(fetchPendingIncomingMaterialTransfers(1));
    dispatch(fetchPendingOutgoingMaterialTransfers(1));
    return () => {
      dispatch(clearMaterialTransferError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearMaterialTransferError());
    }
  }, [error, dispatch]);

  const refreshTransferLists = () => {
    dispatch(fetchPendingIncomingMaterialTransfers(1));
    dispatch(fetchPendingOutgoingMaterialTransfers(1));
    setMaterialsRefreshKey((key) => key + 1);
  };

  const handleCreate = async (
    payload: CreateMaterialTransferPayload,
  ): Promise<boolean> => {
    const result = await dispatch(createMaterialTransfer(payload));
    if (!createMaterialTransfer.fulfilled.match(result)) return false;
    toast.success(`${payload.items.length} material transfer(s) created`);
    refreshTransferLists();
    return true;
  };

  const handleAccept = async (
    transferId: string,
    remark: string,
  ): Promise<boolean> => {
    const result = await dispatch(
      acceptMaterialTransfers([{ transferId, remark }]),
    );
    if (!acceptMaterialTransfers.fulfilled.match(result)) return false;
    toast.success("Material transfer accepted");
    refreshTransferLists();
    return true;
  };

  const handleCancel = async (
    transferId: string,
    remark: string,
  ): Promise<boolean> => {
    const result = await dispatch(
      cancelMaterialTransfers([{ transferId, remark }]),
    );
    if (!cancelMaterialTransfers.fulfilled.match(result)) return false;
    toast.success("Material transfer cancelled");
    refreshTransferLists();
    return true;
  };

  const changeDirection = (direction: "incoming" | "outgoing") => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("materialTransferTab", direction);
    setSearchParams(nextParams, { replace: true });
  };

  const activeList = activeDirection === "incoming" ? incoming : outgoing;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 border-b border-gray-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-[#0077C0]">
            Warehouse
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            Material transfers
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Transfer raw materials between warehouses and track pending stock.
          </p>
        </div>
        <CreateMaterialTransferSection
          warehouseOptions={warehouseOptions}
          isAdmin={isAdmin}
          warehouseAssigned={warehouseAssigned}
          materialsRefreshKey={materialsRefreshKey}
          loading={actionLoading}
          onSubmit={handleCreate}
        />
      </header>

      <section>
        <div
          className="mb-4 grid grid-cols-2 gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1.5 sm:inline-flex sm:gap-1"
          role="tablist"
          aria-label="Material transfer direction"
        >
          <button
            type="button"
            role="tab"
            id="incoming-material-transfers-tab"
            aria-selected={activeDirection === "incoming"}
            aria-controls="incoming-material-transfers-panel"
            onClick={() => changeDirection("incoming")}
            className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors sm:px-5 ${
              activeDirection === "incoming"
                ? "bg-white text-[#0077C0] shadow-sm"
                : "text-gray-500 hover:bg-white/70 hover:text-gray-800"
            }`}
          >
            Incoming
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {incoming.total}
            </span>
          </button>
          <button
            type="button"
            role="tab"
            id="outgoing-material-transfers-tab"
            aria-selected={activeDirection === "outgoing"}
            aria-controls="outgoing-material-transfers-panel"
            onClick={() => changeDirection("outgoing")}
            className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors sm:px-5 ${
              activeDirection === "outgoing"
                ? "bg-white text-[#0077C0] shadow-sm"
                : "text-gray-500 hover:bg-white/70 hover:text-gray-800"
            }`}
          >
            Outgoing
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {outgoing.total}
            </span>
          </button>
        </div>

        <div
          role="tabpanel"
          id={
            activeDirection === "incoming"
              ? "incoming-material-transfers-panel"
              : "outgoing-material-transfers-panel"
          }
          aria-labelledby={
            activeDirection === "incoming"
              ? "incoming-material-transfers-tab"
              : "outgoing-material-transfers-tab"
          }
        >
          <MaterialTransferList
            direction={activeDirection}
            data={activeList}
            actionLoading={actionLoading}
            isAdmin={isAdmin}
            userId={userId}
            onPageChange={(page) =>
              activeDirection === "incoming"
                ? dispatch(fetchPendingIncomingMaterialTransfers(page))
                : dispatch(fetchPendingOutgoingMaterialTransfers(page))
            }
            onAccept={handleAccept}
            onCancel={handleCancel}
          />
        </div>
      </section>
    </div>
  );
};

export default MaterialTransferTab;
